import type { ReactElement } from 'react'
import { WorkflowDeprecatedCheck, WorkflowDisabledTooltip } from 'app/components/workflow'
import { confirmDelete, useRequiredParams } from '@util'
import type { DispositionData, DispositionId } from '@terros/common'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { hasLength, isNotEmpty, useForm } from '@mantine/form'
import { useToastError } from '@hooks'
import { useDisposition, useRemoveDisposition, useTranslation, useUpdateDisposition } from '@front/model'
import {
  ButtonDelete,
  ButtonSave,
  DangerZone,
  NotReady,
  Page,
  TAlert,
  TAuditInfoSection,
  TButton,
  TListItem,
} from '@components'
import { fromDispositionFormData, isValidIcon, toDispositionFormData } from '../settings.disposition_.add/util'
import type { DispositionFormData } from '../settings.disposition_.add/types'
import { DispositionForm } from '../settings.disposition_.add/DispositionForm'
import { DispositionArrowButtons } from './DispositionArrowButtons'

function EditDisposition(): ReactElement {
  const { dispositionId } = useRequiredParams<{ dispositionId: DispositionId }>()
  const { data, isLoading, error } = useDisposition(dispositionId)

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Disposition' isLoading={isLoading} />

  return (
    <WorkflowDeprecatedCheck>
      {({ isWorkflowForCanvassEnabled }) => (
        <EditDispositionWithDispositionData data={data} isWorkflowEnabled={isWorkflowForCanvassEnabled} />
      )}
    </WorkflowDeprecatedCheck>
  )
}

const EditDispositionWithDispositionData = ({
  data,
  isWorkflowEnabled,
}: {
  data: DispositionData
  isWorkflowEnabled: boolean
}): ReactElement => {
  const { t } = useTranslation()
  const isArchived = data.isDeleted
  const { update, isUpdating, error: updateError } = useUpdateDisposition()
  const { remove, isRemoving, error: removeError } = useRemoveDisposition()
  useToastError(updateError)
  useToastError(removeError)
  const navigate = useNavigate()
  const router = useRouter()

  const form = useForm<DispositionFormData<DispositionData>>({
    initialValues: toDispositionFormData(data),
    validate: {
      name: isNotEmpty(t('Name cannot be empty')),
      code: hasLength({ min: 1, max: 4 }, t('Code must be 1-4 characters')),
      color: isNotEmpty(t('Color cannot be empty')),
      icon: (val) => (isValidIcon(val) ? null : t('Icon must be a single emoji or selected icon')),
    },
  })

  const handleSubmit = async (unsaved: DispositionFormData<DispositionData>): Promise<void> => {
    await update(fromDispositionFormData(unsaved))
    router.history.back()
    form.resetDirty(unsaved)
    notifications.show({ message: t('Disposition saved'), color: 'green' })
  }

  const handleArchive = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Archive Disposition'),
      message: t('Are you sure you want to archive this disposition?'),
      label: t('Archive'),
      t,
    })
    if (!confirmed) return

    await remove({ dispositionId: data.dispositionId })
    notifications.show({ message: t('Disposition deleted'), color: 'green' })
    router.history.back()
  }

  const handleUnarchive = async (): Promise<void> => {
    await update({ ...data, isDeleted: false })
    notifications.show({ message: t('Disposition unarchived'), color: 'green' })
    router.history.back()
  }

  if (form.values.scope === 'legacy')
    return (
      <Page>
        <Page.Content>
          <NotReady error={t('Legacy dispositions cannot be edited')} />
        </Page.Content>
      </Page>
    )

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back title={t('Edit Disposition')}>
          <DispositionArrowButtons disposition={data} form={form} />
          <WorkflowDisabledTooltip
            isWorkflowEnabled={isWorkflowEnabled}
            conditions={[{ active: !form.isDirty(), label: t('No changes to save.') }]}
          >
            <ButtonSave disabled={!form.isDirty()} loading={isUpdating} type='submit' />
          </WorkflowDisabledTooltip>
        </Page.Heading>

        <Page.Content scrollArea>
          {isArchived && (
            <TAlert variant='warning'>
              <div className='flex flex-col items-start'>
                {t('This disposition is archived.')}
                <TButton variant='secondary' onClick={handleUnarchive}>
                  {t('Unarchive')}
                </TButton>
              </div>
            </TAlert>
          )}
          <DispositionForm form={form} />
          <TAuditInfoSection {...data}>
            <TListItem canCopy detailBold details={data.dispositionId} label={t('Disposition ID')} />
          </TAuditInfoSection>
          {!isArchived && !isWorkflowEnabled && (
            <DangerZone>
              <ButtonDelete loading={isRemoving} onClick={handleArchive}>
                {t('Archive')}
              </ButtonDelete>
            </DangerZone>
          )}
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/disposition_/$dispositionId/')({
  component: EditDisposition,
})
